import { gql } from "@apollo/client";

// Candidate triage status
export const CandidateTriageStatusApprovedByRecruiter = "ApprovedByRecruiter";
export const CandidateTriageStatusPassedByRecruiter = "PassedByRecruiter";
export const CandidateTriageStatusApprovedByClient = "ApprovedByClient";
export const CandidateTriageStatusPassedByClient = "PassedByClient";
export const CandidateTriageStatusHired = "Hired";

export const MoblyzeClosedReasonCreatedAccidentally = "CreatedAccidentally";
export const MoblyzeClosedReasonCancelledByClient = "CancelledByClient";
export const MoblyzeClosedReasonFilledByUs = "FilledByUs";
export const MoblyzeClosedReasonFilledByCompetitor = "FilledByCompetitor";
export const MoblyzeClosedReasonOther = "Other";
export const MoblyzeClosedReasonFilledByClient = "FilledByClient";

export const MoblyzeClosedReasons = [
  MoblyzeClosedReasonCreatedAccidentally,
  MoblyzeClosedReasonCancelledByClient,
  MoblyzeClosedReasonFilledByUs,
  MoblyzeClosedReasonFilledByCompetitor,
  MoblyzeClosedReasonOther,
  MoblyzeClosedReasonFilledByClient,
];

// Availability signals on a candidate profile. A decaying declaration, not a
// fact: src/helpers/availability.js reduces these to one displayable state
// and the pipeline filter reads them the same way. Every query feeding a view
// that shows availability must spread this, or the line silently renders
// nothing (asserted in jobs.test.jsx).
export const CANDIDATE_AVAILABILITY_FRAGMENT = gql`
  fragment CandidateAvailabilityFragment on CandidateProfile {
    id
    activelyLookingSetAt
    availability {
      status
      availableFrom
      availableUntil
      setAt
      freshness
    }
  }
`;

export const CANDIDATE_FRAGMENT = gql`
  ${CANDIDATE_AVAILABILITY_FRAGMENT}
  fragment CandidateFragment on Candidate {
    candidateProfileId
    isExternal
    userId
    firstName
    lastName
    fullName
    email
    primaryPhone

    referralCode
    referrals {
      id
      createdAt
    }

    candidateProfile {
      id
      activelyLooking
      ...CandidateAvailabilityFragment
      linkedInURL
      resumeSummary
      # Bullhorn-sourced text about the candidate, frequently a CV rather than a
      # blurb. Rendered as the "Bullhorn Summary" in the slot the AI summary
      # would occupy when there is no resumeSummary — see CandidateSummaryPanel.
      #
      # These three are null unless the viewer is a recruiter whose agency
      # covers the profile, so the client portal never receives them and any
      # surface built on them must tolerate null.
      externalDescription
      bullhornId
      bullhornUrl
      certification {
        id
        name
      }

      agencySlugId
    }
  }
`;

// Signed URL fields are fetched separately so the triage poll never
// overwrites the cached URLs (they rotate on every response).
export const CANDIDATE_URLS_FRAGMENT = gql`
  fragment CandidateUrlsFragment on Candidate {
    candidateProfileId
    profileImageUrl
    candidateProfile {
      id
      resumeUrl
      resumePreviewUrl
      agencyLogoUrl
      certification {
        id
        url
      }
    }
  }
`;

// Slim signed-URL fragment for the triage list: only the profile image is
// needed for list rows and the selected-candidate header.
export const CANDIDATE_TRIAGE_LIST_URLS_FRAGMENT = gql`
  fragment CandidateTriageListUrlsFragment on Candidate {
    candidateProfileId
    profileImageUrl
  }
`;

export const CANDIDATE_TRIAGE_FRAGMENT = gql`
  ${CANDIDATE_FRAGMENT}
  fragment CandidateTriageFragment on CandidateTriage {
    id
    moblyzeJobId
    status
    clientNotes
    candidate {
      ...CandidateFragment
    }

    matchScore
    matchingStatus
    matchingCriteriaCategories {
      slug
      name
      requirements {
        criterionId
        matches
        isDefinitive
        reason
        isStale
      }
    }
  }
`;

// Slim fragment used to populate the triage list and apply filters. Only the
// fields needed to render a list row, sort by match score, filter by
// criterion or availability, and open chat (userId) are included — full candidate detail is
// fetched on demand via AGENCY_CANDIDATE_DETAIL_QUERY when a candidate is
// selected.
export const CANDIDATE_TRIAGE_LIST_FRAGMENT = gql`
  ${CANDIDATE_AVAILABILITY_FRAGMENT}
  fragment CandidateTriageListFragment on Candidate {
    candidateProfileId
    userId
    fullName
    isExternal
    # Rows show the availability line and the list filters on it.
    candidateProfile {
      ...CandidateAvailabilityFragment
    }
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_REPORT_FRAGMENT = gql`
  ${CANDIDATE_TRIAGE_LIST_FRAGMENT}

  fragment AgencyCandidateTriageReportFragment on AgencyCandidateTriageReport {
    candidate {
      ...CandidateTriageListFragment
    }

    matchScore
    viewedByRecruiter
    favourite
    invited
    isInternalTestUser
    matchingStatus

    # Recommendations. Only the Suggested list sets these (every other list
    # returns recommended: false); it puts recommended candidates first.
    recommended
    recommendedBy
    recommendedSource
    recommendedReason
    recommendedAt

    matchingCriteriaCategories {
      slug
      name
      requirements {
        criterionId
        matches
        isDefinitive
        reason
        isStale
      }
    }
  }
`;

export const MOBLYZE_JOB_FRAGMENT = gql`
  fragment MoblyzeJobFragment on MoblyzeJob {
    id

    bullhornId

    status
    criteriaExtractionStatus

    category
    title

    employmentType
    numOpenings

    clientOrganizationId
    employerName
    employerLogoUrl

    employerContactName
    employerContactEmail
    employerContactPhone
    agencyContactName
    agencyContactEmail
    agencyContactPhone

    location

    duration
    startDate
    startDateText
    isAsap

    requirementsDescription
    responsibilitiesDescription
    otherDescription

    coverImageUrl

    vesselId
    vessel {
      id
      name
      subtitle
      flag
      ageDescription
      wifiSpeedDescription
      isSmoking
      totalCrew
      vesselUrl
      imageUrl
    }

    closedReason
    closedNote
    reactivationNote

    hiredInstructions
    siteAddress

    roles {
      id
      name
    }

    workLocations {
      id
      name
    }

    criteriaCategories {
      slug
      name
      criteria {
        id
        title
        description
        isRequired
      }
    }

    payRateValue
    payFrequency
    payCurrency

    createdAt
    updatedAt
  }
`;

export const MOBLYZE_JOB_LIST_FRAGMENT = gql`
  fragment MoblyzeJobListFragment on MoblyzeJob {
    id

    bullhornId

    category
    title

    employerName

    location

    startDate
    startDateText
    isAsap

    agencyContactEmail
    employerContactEmail

    createdAt
  }
`;

export const ACTIVE_JOBS_QUERY = gql`
  ${MOBLYZE_JOB_LIST_FRAGMENT}
  query AgencyActiveJobs($contactEmail: String) {
    agencyActiveJobs(contactEmail: $contactEmail) {
      ...MoblyzeJobListFragment

      recruiterJobReport {
        interestedCount
        shortlistedCount
        unviewedInterestedCount
        approvedByClientCount
        approvedByClientUnviewedCount
      }
    }
  }
`;

export const ARCHIVED_JOBS_QUERY = gql`
  ${MOBLYZE_JOB_LIST_FRAGMENT}
  query AgencyArchivedJobs($contactEmail: String) {
    agencyArchivedJobs(contactEmail: $contactEmail) {
      ...MoblyzeJobListFragment
    }
  }
`;

export const AGENCY_JOB_DETAILS_QUERY = gql`
  ${MOBLYZE_JOB_FRAGMENT}
  query MoblyzeJob($id: ID!) {
    moblyzeJob(id: $id) {
      ...MoblyzeJobFragment
      clientNotificationSettings {
        frequency
        dailySendHour
        timezone
        recipientEmails
      }
      # The job's owning Mya thread. Resolves only for the job's owner and for
      # Moblyze admins (who can read its transcript); null for anyone else.
      ownerMyaThreadId
    }
  }
`;

export const MOBLYZE_JOB_BY_BULLHORN_ID_QUERY = gql`
  query MoblyzeJobByBullhornId($bullhornId: String!) {
    moblyzeJobByBullhornId(bullhornId: $bullhornId) {
      id
      status
    }
  }
`;

// On-demand Bullhorn import for the embed page: ask for the job order to be
// fetched and assessed now, then poll the status until it leaves InProgress.
export const REQUEST_BULLHORN_JOB_IMPORT_MUTATION = gql`
  mutation RequestBullhornJobImport($bullhornId: String!) {
    requestBullhornJobImport(bullhornId: $bullhornId) {
      bullhornId
      state
      willCreate
      mode
      moblyzeJobId
      jobStatus
      gateFailed
      message
    }
  }
`;

// Whether the viewer may use the Moblyze tab in Bullhorn at all. Asked before
// any import, so the tab can explain itself instead of erroring or spinning.
export const BULLHORN_EMBED_ACCESS_QUERY = gql`
  query BullhornEmbedAccess {
    bullhornEmbedAccess {
      allowed
      reason
      viewerEmail
      canSwitchAgency
      requiredAgencySlugId
      requiredAgencyName
    }
  }
`;

export const BULLHORN_JOB_IMPORT_STATUS_QUERY = gql`
  query BullhornJobImportStatus($bullhornId: String!) {
    bullhornJobImportStatus(bullhornId: $bullhornId) {
      bullhornId
      state
      willCreate
      mode
      moblyzeJobId
      jobStatus
      gateFailed
      message
      judgeVerdict
      judgeReasoning
      missingRequiredFields
      decidedAt
    }
  }
`;

export const AGENCY_JOB_CANDIDATE_TRIAGE_QUERY = gql`
  ${AGENCY_CANDIDATE_TRIAGE_REPORT_FRAGMENT}
  query MoblyzeJob($id: ID!) {
    moblyzeJob(id: $id) {
      id
      matchingProgress
    }
    agencyCandidateTriageSuggestedCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
    agencyCandidateTriageInterestedCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
    agencyCandidateTriageShortlistedCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
    agencyCandidateTriageApprovedCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
    agencyCandidateTriageHiredCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
    agencyCandidateTriageRejectedCandidates(moblyzeJobId: $id) {
      ...AgencyCandidateTriageReportFragment
    }
  }
`;

export const AGENCY_JOB_CANDIDATE_URLS_QUERY = gql`
  ${CANDIDATE_TRIAGE_LIST_URLS_FRAGMENT}
  query AgencyJobCandidateUrls($id: ID!) {
    agencyCandidateTriageSuggestedCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
    agencyCandidateTriageInterestedCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
    agencyCandidateTriageShortlistedCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
    agencyCandidateTriageApprovedCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
    agencyCandidateTriageHiredCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
    agencyCandidateTriageRejectedCandidates(moblyzeJobId: $id) {
      candidate { ...CandidateTriageListUrlsFragment }
    }
  }
`;

// Detail query for a single candidate. Loaded on demand when a triage row is
// selected so the list query stays small.
export const AGENCY_CANDIDATE_DETAIL_QUERY = gql`
  ${CANDIDATE_FRAGMENT}
  ${CANDIDATE_URLS_FRAGMENT}
  query AgencyCandidateDetail($candidateProfileId: ID!) {
    candidate(candidateProfileId: $candidateProfileId) {
      ...CandidateFragment
      ...CandidateUrlsFragment
    }
  }
`;

export const AGENCY_CLOSE_JOB_MUTATION = gql`
  mutation CloseJob(
    $moblyzeJobId: ID!
    $reason: MoblyzeClosedReason!
    $closedNote: String!
  ) {
    closeJob(
      moblyzeJobId: $moblyzeJobId
      reason: $reason
      closedNote: $closedNote
    ) {
      id
    }
  }
`;

export const AGENCY_REACTIVATE_JOB_MUTATION = gql`
  mutation ReactivateJob($moblyzeJobId: ID!, $reactivationNote: String!) {
    reactivateJob(
      moblyzeJobId: $moblyzeJobId
      reactivationNote: $reactivationNote
    ) {
      id
    }
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_ADD_TO_SHORTLIST_MUTATION = gql`
  mutation CandidateTriageAddToShortlist(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageAddToShortlist(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_SEND_INVITE_MUTATION = gql`
  mutation CandidateTriageSendInvite(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageSendInvite(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_REMOVE_FROM_SHORTLIST_MUTATION = gql`
  mutation CandidateTriageRemoveFromShortlist(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageRemoveFromShortlist(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_ADD_TO_PASS_LIST_MUTATION = gql`
  mutation CandidateTriageAddToPassList(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageAddToPassList(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

// Recommend a candidate for a job: they are starred at the top of its
// Suggested list with the (optional) reason. Recommending again updates the
// reason.
export const AGENCY_CANDIDATE_TRIAGE_RECOMMEND_MUTATION = gql`
  mutation CandidateTriageRecommend(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
    $reason: String
  ) {
    candidateTriageRecommend(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
      reason: $reason
    )
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_UNRECOMMEND_MUTATION = gql`
  mutation CandidateTriageUnrecommend(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageUnrecommend(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const AGENCY_CANDIDATE_TRIAGE_MARK_AS_HIRED_MUTATION = gql`
  mutation CandidateTriageMarkAsHired(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    candidateTriageMarkAsHired(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const CLIENT_NOTES_FOR_CANDIDATE_TRIAGE_QUERY = gql`
  query ClientNotesForCandidateTriage(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    clientNotesForCandidateTriage(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

// Client queries and mutations

export const CLIENT_JOB_DETAILS_QUERY = gql`
  ${MOBLYZE_JOB_FRAGMENT}
  ${CANDIDATE_TRIAGE_FRAGMENT}
  ${CANDIDATE_URLS_FRAGMENT}
  query JobDetails($id: ID!) {
    moblyzeJob(id: $id) {
      ...MoblyzeJobFragment
      clientNotificationSettings {
        frequency
        dailySendHour
        timezone
        recipientEmails
      }
    }

    clientTriageListForJob(moblyzeJobId: $id) {
      ...CandidateTriageFragment
      candidate {
        ...CandidateUrlsFragment
      }
    }
  }
`;

export const CLIENT_ACTIVE_JOBS_QUERY = gql`
  ${MOBLYZE_JOB_FRAGMENT}
  {
    clientActiveJobs {
      ...MoblyzeJobFragment
    }
  }
`;

export const CLIENT_PASS_ON_CANDIDATE_FOR_JOB_MUTATION = gql`
  mutation ClientPassOnCandidateForJob(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
  ) {
    clientPassOnCandidateForJob(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const CLIENT_APPROVE_CANDIDATE_FOR_JOB_MUTATION = gql`
  mutation ClientApproveCandidateForJob(
    $moblyzeJobId: ID!
    $candidateProfileId: ID!
    $note: String
  ) {
    clientApproveCandidateForJob(
      moblyzeJobId: $moblyzeJobId
      candidateProfileId: $candidateProfileId
      note: $note
    )
  }
`;

export const CLIENT_SET_JOB_NOTIFICATION_SETTINGS_MUTATION = gql`
  mutation ClientSetJobNotificationSettings(
    $moblyzeJobId: ID!
    $input: ClientJobNotificationSettingsInput!
  ) {
    clientSetJobNotificationSettings(moblyzeJobId: $moblyzeJobId, input: $input) {
      id
      clientNotificationSettings {
        frequency
        dailySendHour
        timezone
        recipientEmails
      }
    }
  }
`;

export const JOB_CRITERIA_QUERY = gql`
  query MoblyzeJobCriteria($id: ID!) {
    moblyzeJob(id: $id) {
      id
      criteriaExtractionStatus
      criteriaCategories {
        slug
        name
        criteria {
          id
          title
          description
          isRequired
        }
      }
    }
  }
`;

export const ADD_JOB_CRITERION_MUTATION = gql`
  mutation AddJobCriterion($moblyzeJobId: ID!, $input: CriterionInput!) {
    addJobCriterion(moblyzeJobId: $moblyzeJobId, input: $input) {
      id
      title
      description
      isRequired
    }
  }
`;

export const UPDATE_JOB_CRITERION_MUTATION = gql`
  mutation UpdateJobCriterion(
    $moblyzeJobId: ID!
    $criterionId: ID!
    $input: CriterionInput!
  ) {
    updateJobCriterion(
      moblyzeJobId: $moblyzeJobId
      criterionId: $criterionId
      input: $input
    ) {
      id
      title
      description
      isRequired
    }
  }
`;

export const REMOVE_JOB_CRITERION_MUTATION = gql`
  mutation RemoveJobCriterion($moblyzeJobId: ID!, $criterionId: ID!) {
    removeJobCriterion(moblyzeJobId: $moblyzeJobId, criterionId: $criterionId)
  }
`;

export const CANDIDATE_SKILLS_FRAGMENT = gql`
  fragment CandidateSkillsFragment on CandidateRolePreferenceResponse {
    candidateRoles {
      id
      name
    }
    skills {
      id
      name
    }
  }
`;

export const CLIENT_CANDIDATE_SKILLS_QUERY = gql`
  ${CANDIDATE_SKILLS_FRAGMENT}
  query ClientCandidateSkills($candidateProfileId: ID!) {
    getCandidateSkillsByProfileId(candidateProfileId: $candidateProfileId) {
      ...CandidateSkillsFragment
    }
  }
`;

export const GET_CERTIFICATIONS_TYPE = gql`
  query GetCertifications {
    getCertifications {
      id
      name
    }
  }
`;

export const GET_CERTIFICATIONS_BY_MOBLYZE_JOB_ID_QUERY = gql`
  query GetCertificationsByMoblyzeJobID($moblyzeJobId: ID!) {
    getCertificationsByMoblyzeJobId(moblyzeJobId: $moblyzeJobId) {
      name
    }
  }
`;

export const CANDIDATE_CERTIFICATE_UPLOAD_MUTATION = gql`
  mutation UploadCandidateCertification(
    $candidateProfileId: ID!
    $file: Upload!
    $name: String!
    $expiry: Time
    $issue: Time
  ) {
    uploadCandidateCertification(
      candidateProfileId: $candidateProfileId
      file: $file
      name: $name
      expiry: $expiry
      issue: $issue
    ) {
      candidateProfileId
      candidateProfile {
        id
      }
    }
  }
`;

export const CANDIDATE_CERTIFICATE_DELETE_MUTATION = gql`
  mutation DeleteCandidateCertification($id: ID!, $candidateProfileId: ID) {
    deleteCandidateCertification(id: $id, candidateProfileId: $candidateProfileId) {
      candidateProfileId
    }
  }
`;

// Recruiter view of a job's new-applicant emails: null unless the viewer is a
// recruiter and the job's agency gets its emails from the API; otherwise
// whether each new applicant is emailed (in 15-minute batches) to the job's
// owner, who receives them only if their role is Recruiter. Fetched on its
// own rather than on the job query so the job page keeps loading against an
// API without the field. Mirrored in moblyze-clients/src/graphql/.
export const JOB_APPLICANT_EMAILS_QUERY = gql`
  query JobApplicantEmails($id: ID!) {
    moblyzeJob(id: $id) {
      id
      applicantEmails {
        enabled
      }
      owner {
        id
        firstName
        lastName
        role
      }
    }
  }
`;

export const SET_JOB_APPLICANT_EMAILS_MUTATION = gql`
  mutation SetJobApplicantEmails($moblyzeJobId: ID!, $enabled: Boolean!) {
    setJobApplicantEmails(moblyzeJobId: $moblyzeJobId, enabled: $enabled) {
      id
      applicantEmails {
        enabled
      }
    }
  }
`;

// Client view of a job's one email, the shortlist alert: null unless the
// viewer is one of the job's client members and the job's agency gets its
// emails from the API; otherwise whether each candidate shortlisted on the
// job is emailed to all the client's members. Fetched on its own for the same
// reason as JOB_APPLICANT_EMAILS_QUERY.
export const JOB_SHORTLIST_EMAILS_QUERY = gql`
  query JobShortlistEmails($id: ID!) {
    moblyzeJob(id: $id) {
      id
      shortlistEmails {
        enabled
      }
    }
  }
`;

export const SET_JOB_SHORTLIST_EMAILS_MUTATION = gql`
  mutation SetJobShortlistEmails($moblyzeJobId: ID!, $enabled: Boolean!) {
    setJobShortlistEmails(moblyzeJobId: $moblyzeJobId, enabled: $enabled) {
      id
      shortlistEmails {
        enabled
      }
    }
  }
`;
